import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Edit2, Trash2, X, Upload, Loader2, Image as ImageIcon, Sparkles, Languages, Search, ChevronDown, ChevronRight, Layers, MoveRight, GripVertical, CheckSquare, Square
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import { toast } from 'sonner';
import CategoryManagement from './CategoryManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyFormData = {
  name: '', description: '', price: '', category: '', image_url: '',
  is_available: true, show_in_catering: false, catering_category: '',
  is_featured: false,
  name_en: '', name_me: '', name_ru: '', name_sr: '',
  description_en: '', description_me: '', description_ru: '', description_sr: ''
};

const MenuManagement = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [bulkTranslating, setBulkTranslating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [categoryList, setCategoryList] = useState([]);  // From backend
  const [showCategoryPanel, setShowCategoryPanel] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkCatDialog, setShowBulkCatDialog] = useState(false);
  const [bulkTargetCat, setBulkTargetCat] = useState('');
  const [dragItemId, setDragItemId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const fileInputRef = useRef(null);

  // Derived: category name list + display map for select boxes / group headers
  const categories = useMemo(() => (categoryList.length ? categoryList.map(c => c.name) : []), [categoryList]);
  const catDisplayNames = useMemo(() => {
    const m = {};
    categoryList.forEach(c => { m[c.name] = c.name; });
    return m;
  }, [categoryList]);
  const cateringCategories = [
    { value: 'corba', label: 'Corba' },
    { value: 'ana_yemek', label: 'Ana Yemek' },
    { value: 'yardimci_yemek', label: 'Yardimci Yemek' },
    { value: 'salata', label: 'Salata' },
    { value: 'meze', label: 'Meze' }
  ];

  useEffect(() => { fetchMenu(); fetchCategories(); }, []);

  const fetchMenu = async () => {
    try {
      const res = await axios.get(`${API}/admin/menu`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch { toast.error('Menü yüklenemedi'); } finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/menu-categories`);
      setCategoryList(res.data || []);
    } catch { /* silent — legacy fallback works */ }
  };

  const handleBulkTranslate = async () => {
    if (!confirm('Tüm menü ürünleri, içecekler ve catering öğeleri için eksik dilleri (EN/ME/RU/SR) AI ile otomatik tamamlamak ister misiniz? Bu işlem 1-3 dakika sürebilir.')) return;
    setBulkTranslating(true);
    const toastId = toast.loading('AI tüm ürünleri tarıyor ve eksik dilleri çeviriyor... Bu işlem birkaç dakika sürebilir.');
    try {
      const res = await axios.post(`${API}/admin/translate-bulk-missing`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 600000
      });
      const p = res.data.processed || {};
      const total = (p.menu_items || 0) + (p.beverages || 0) + (p.catering || 0);
      toast.success(
        `Çeviri tamamlandı! Menü: ${p.menu_items || 0}, İçecek: ${p.beverages || 0}, Catering: ${p.catering || 0} ürün güncellendi.${p.errors ? ` (${p.errors} hata)` : ''}`,
        { id: toastId, duration: 8000 }
      );
      if (total > 0) fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toplu çeviri başarısız', { id: toastId });
    } finally {
      setBulkTranslating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Resim yüklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resim yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleTranslate = async () => {
    if (!formData.name) { toast.error('Önce ürün adını girin'); return; }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/admin/catering/translate`, {
        name: formData.name,
        category: formData.category || 'genel'
      }, { headers: { Authorization: `Bearer ${token}` } });
      setFormData(prev => ({
        ...prev,
        name_en: res.data.name_en || prev.name_en,
        name_me: res.data.name_me || prev.name_me,
        name_ru: res.data.name_ru || prev.name_ru,
        name_sr: res.data.name_sr || prev.name_sr,
        description: res.data.description || prev.description,
        description_en: res.data.description_en || prev.description_en,
        description_me: res.data.description_me || prev.description_me,
        description_ru: res.data.description_ru || prev.description_ru,
        description_sr: res.data.description_sr || prev.description_sr
      }));
      toast.success('AI çeviri tamamlandı (5 dil)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çeviri başarısız');
    } finally { setTranslating(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price || !formData.category) { toast.error('Gerekli alanları doldurun'); return; }
    try {
      let data = { ...formData };

      // Auto-translate any missing languages on save (silent best-effort)
      const missingLangs = ['en', 'me', 'ru', 'sr'].some(l => !data[`name_${l}`]);
      if (missingLangs) {
        try {
          const tr = await axios.post(`${API}/admin/catering/translate`, {
            name: data.name, category: data.category || 'genel'
          }, { headers: { Authorization: `Bearer ${token}` } });
          ['en', 'me', 'ru', 'sr'].forEach(l => {
            if (!data[`name_${l}`] && tr.data[`name_${l}`]) data[`name_${l}`] = tr.data[`name_${l}`];
            if (!data[`description_${l}`] && tr.data[`description_${l}`]) data[`description_${l}`] = tr.data[`description_${l}`];
          });
        } catch { /* non-blocking */ }
      }

      data = {
        ...data,
        price: parseFloat(data.price),
        is_featured: !!data.is_featured,
        name_en: data.name_en || null,
        name_me: data.name_me || null,
        name_ru: data.name_ru || null,
        name_sr: data.name_sr || null,
        description_en: data.description_en || null,
        description_me: data.description_me || null,
        description_ru: data.description_ru || null,
        description_sr: data.description_sr || null,
        show_in_catering: data.show_in_catering || false,
        catering_category: data.show_in_catering ? (data.catering_category || null) : null
      };

      if (editingItem) {
        await axios.put(`${API}/admin/menu/${editingItem.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ürün güncellendi');
      } else {
        await axios.post(`${API}/admin/menu`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ürün eklendi');
      }
      setShowDialog(false); setEditingItem(null);
      setFormData(emptyFormData);
      fetchMenu();
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ 
      name: item.name, 
      description: item.description, 
      price: item.price.toString(), 
      category: item.category, 
      image_url: item.image_url, 
      is_available: item.is_available !== false,
      show_in_catering: item.show_in_catering || false,
      catering_category: item.catering_category || '',
      is_featured: item.is_featured || false,
      name_en: item.name_en || '',
      name_me: item.name_me || '',
      name_ru: item.name_ru || '',
      name_sr: item.name_sr || '',
      description_en: item.description_en || '',
      description_me: item.description_me || '',
      description_ru: item.description_ru || '',
      description_sr: item.description_sr || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/menu/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Silindi'); fetchMenu();
    } catch { toast.error('Silinemedi'); }
  };

  const toggleGroup = (cat) => setCollapsedGroups(prev => ({ ...prev, [cat]: !prev[cat] }));

  // === Multi-select ===
  const enterSelectMode = () => { setSelectMode(true); setSelectedIds([]); };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds([]); };
  const toggleSelected = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAllInGroup = (groupIds) => {
    setSelectedIds(prev => Array.from(new Set([...prev, ...groupIds])));
  };
  const openBulkCatDialog = () => {
    if (selectedIds.length === 0) return toast.error('Önce ürün seçin');
    setBulkTargetCat('');
    setShowBulkCatDialog(true);
  };
  const submitBulkCategory = async () => {
    if (!bulkTargetCat) return toast.error('Hedef kategori seçin');
    try {
      const res = await axios.post(`${API}/admin/menu/bulk-category`,
        { item_ids: selectedIds, category: bulkTargetCat },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data?.updated || selectedIds.length} ürün → ${bulkTargetCat}`);
      setShowBulkCatDialog(false);
      exitSelectMode();
      fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toplu değişim başarısız');
    }
  };

  // === Drag & Drop reorder within same category ===
  const onDragStart = (id) => setDragItemId(id);
  const onDragOverItem = (e, id) => { e.preventDefault(); setDragOverId(id); };
  const onDragEnd = () => { setDragItemId(null); setDragOverId(null); };

  const persistReorder = async (reorderList) => {
    try {
      await axios.post(`${API}/admin/menu/reorder`,
        { items: reorderList.map((it, i) => ({ id: it.id, display_order: i })) },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch { toast.error('Sıralama kaydedilemedi'); }
  };

  const onDropItem = (targetId, groupItems) => {
    if (!dragItemId || dragItemId === targetId) { onDragEnd(); return; }
    const src = groupItems.find(x => x.id === dragItemId);
    const tgt = groupItems.find(x => x.id === targetId);
    if (!src || !tgt || src.category !== tgt.category) {
      toast.error('Yalnızca aynı kategori içinde sürükleyip bırakabilirsiniz');
      onDragEnd();
      return;
    }
    const next = [...groupItems];
    const srcIdx = next.findIndex(x => x.id === dragItemId);
    const tgtIdx = next.findIndex(x => x.id === targetId);
    next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, src);
    // Update local items ordering (only within this category)
    setItems(prev => {
      const others = prev.filter(x => x.category !== src.category);
      return [...others, ...next.map((it, i) => ({ ...it, display_order: i }))];
    });
    persistReorder(next);
    onDragEnd();
  };

  // Search + group by category
  const groupedItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? items.filter(it => {
          const hay = [
            it.name, it.name_en, it.name_me, it.name_ru, it.name_sr,
            it.description, it.category, it.catering_category
          ].filter(Boolean).join(' ').toLowerCase();
          return hay.includes(q);
        })
      : items;

    const groups = {};
    for (const it of filtered) {
      const cat = it.category || 'Diğer';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(it);
    }
    // Sort each group by display_order (stable fallback by name)
    Object.keys(groups).forEach(k => {
      groups[k].sort((a, b) => {
        const oa = Number(a.display_order) || 0;
        const ob = Number(b.display_order) || 0;
        if (oa !== ob) return oa - ob;
        return (a.name || '').localeCompare(b.name || '');
      });
    });
    // Ordered category list: managed order first, then unknown alphabetical
    const known = categories.filter(c => groups[c] && groups[c].length > 0);
    const unknown = Object.keys(groups).filter(c => !categories.includes(c)).sort();
    return { orderedCats: [...known, ...unknown], groups, totalMatched: filtered.length };
  }, [items, searchQuery, categories]);

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-playfair text-xl font-semibold">Menü Yönetimi</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant="outline"
            onClick={() => setShowCategoryPanel(true)}
            className="rounded-full border-stone-300"
            data-testid="open-categories-btn">
            <Layers className="w-4 h-4 mr-2" />Kategoriler
          </Button>
          {selectMode ? (
            <>
              <span className="text-xs font-semibold text-terracotta bg-terracotta/10 px-3 py-1.5 rounded-full" data-testid="selected-count-badge">
                {selectedIds.length} seçili
              </span>
              <Button size="sm" onClick={openBulkCatDialog} disabled={!selectedIds.length}
                className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full disabled:opacity-50"
                data-testid="bulk-cat-btn">
                <MoveRight className="w-4 h-4 mr-1" />Kategori Değiştir
              </Button>
              <Button size="sm" variant="outline" onClick={exitSelectMode} className="rounded-full" data-testid="exit-select-btn">Vazgeç</Button>
            </>
          ) : (
            <Button type="button" variant="outline" onClick={enterSelectMode}
              className="rounded-full border-stone-300" data-testid="enter-select-btn">
              <CheckSquare className="w-4 h-4 mr-2" />Toplu Seç
            </Button>
          )}
          <Button
            type="button" variant="outline"
            onClick={handleBulkTranslate} disabled={bulkTranslating}
            className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="bulk-translate-btn"
          >
            {bulkTranslating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Languages className="w-4 h-4 mr-2" />}
            {bulkTranslating ? 'Çevriliyor...' : 'Tüm Dilleri Yenile'}
          </Button>
          <Button onClick={() => { setEditingItem(null); setFormData(emptyFormData); setShowDialog(true); }} className="bg-basil text-white hover:bg-basil/90 rounded-full" data-testid="new-item-btn"><Plus className="w-4 h-4 mr-2" />Yeni Ürün</Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Ürün, kategori veya açıklamada ara..."
            className="pl-9 pr-9"
            data-testid="menu-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-stone-100 rounded-full text-charcoal/50"
              data-testid="menu-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <span className="text-xs text-charcoal/60" data-testid="menu-search-count">
          {searchQuery ? `${groupedItems.totalMatched} eşleşme` : `${items.length} ürün`}
        </span>
      </div>
      <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setEditingItem(null); setFormData(emptyFormData); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingItem ? 'Ürün Düzenle' : 'Yeni Ürün'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label>Ürün Adı (Türkçe) *</Label>
                  <Button type="button" size="sm" variant="outline" onClick={handleTranslate} disabled={translating || !formData.name}
                    className="text-xs rounded-full h-7 px-3 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="menu-item-translate-btn">
                    {translating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    {translating ? 'Çevriliyor...' : 'AI Çevir'}
                  </Button>
                </div>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Ürün Adı (English)</Label><Input value={formData.name_en || ''} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} className="mt-1" placeholder="English name" /></div>
                <div><Label>Ürün Adı (Crnogorski)</Label><Input value={formData.name_me || ''} onChange={(e) => setFormData({ ...formData, name_me: e.target.value })} className="mt-1" placeholder="Montenegrin name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Ürün Adı (Русский)</Label><Input value={formData.name_ru || ''} onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })} className="mt-1" placeholder="Russian name (Cyrillic)" data-testid="menu-item-name-ru" /></div>
                <div><Label>Ürün Adı (Српски)</Label><Input value={formData.name_sr || ''} onChange={(e) => setFormData({ ...formData, name_sr: e.target.value })} className="mt-1" placeholder="Serbian name (Cyrillic)" data-testid="menu-item-name-sr" /></div>
              </div>
              <div><Label>Açıklama (Türkçe)</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Açıklama (English)</Label><Textarea value={formData.description_en || ''} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="mt-1" placeholder="English description" rows={2} /></div>
                <div><Label>Açıklama (Crnogorski)</Label><Textarea value={formData.description_me || ''} onChange={(e) => setFormData({ ...formData, description_me: e.target.value })} className="mt-1" placeholder="Montenegrin description" rows={2} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Açıklama (Русский)</Label><Textarea value={formData.description_ru || ''} onChange={(e) => setFormData({ ...formData, description_ru: e.target.value })} className="mt-1" placeholder="Russian description (Cyrillic)" rows={2} data-testid="menu-item-desc-ru" /></div>
                <div><Label>Açıklama (Српски)</Label><Textarea value={formData.description_sr || ''} onChange={(e) => setFormData({ ...formData, description_sr: e.target.value })} className="mt-1" placeholder="Serbian description (Cyrillic)" rows={2} data-testid="menu-item-desc-sr" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Fiyat (€)</Label><Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="mt-1" /></div>
                <div><Label>Kategori</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{catDisplayNames[c] || c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Ürün Görseli</Label>
                <div className="mt-2 space-y-3">
                  {formData.image_url && (
                    <div className="relative w-full h-32 rounded-lg overflow-hidden bg-stone-100">
                      <img src={formData.image_url.startsWith('/api') ? `${API.replace('/api', '')}${formData.image_url}` : formData.image_url} alt="Önizleme" className="w-full h-full object-cover" />
                      <button onClick={() => setFormData({ ...formData, image_url: '' })} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                    <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex-1">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {uploading ? 'Yükleniyor...' : 'Dosya Seç'}
                    </Button>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-charcoal/50">veya URL girin</span></div>
                  </div>
                  <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              {/* Availability & Catering toggles */}
              <div className="grid grid-cols-1 gap-3 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">Satista</Label>
                  <Switch checked={formData.is_available} onCheckedChange={(v) => setFormData({ ...formData, is_available: v })} data-testid="menu-item-available" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <span className="text-amber-600">⭐</span> Şefin Önerisi
                  </Label>
                  <Switch checked={formData.is_featured} onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })} data-testid="menu-item-featured" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">Catering Menusunde Goster</Label>
                  <Switch checked={formData.show_in_catering} onCheckedChange={(v) => setFormData({ ...formData, show_in_catering: v, catering_category: v ? formData.catering_category : '' })} data-testid="menu-item-catering" />
                </div>
                {formData.show_in_catering && (
                  <div>
                    <Label>Catering Kategorisi</Label>
                    <Select value={formData.catering_category} onValueChange={(v) => setFormData({ ...formData, catering_category: v })}>
                      <SelectTrigger className="mt-1" data-testid="menu-item-catering-cat"><SelectValue placeholder="Kategori secin" /></SelectTrigger>
                      <SelectContent>{cateringCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button onClick={handleSubmit} className="w-full bg-terracotta text-white hover:bg-terracotta/90">{editingItem ? 'Güncelle' : 'Ekle'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      {groupedItems.orderedCats.length === 0 ? (
        <div className="text-center py-12 text-charcoal/50 bg-stone-50 rounded-xl" data-testid="menu-empty-state">
          {searchQuery ? `"${searchQuery}" ile eşleşen ürün bulunamadı.` : 'Henüz ürün yok. Yeni Ürün ekleyin.'}
        </div>
      ) : (
        <div className="space-y-6">
          {groupedItems.orderedCats.map(cat => {
            const groupItems = groupedItems.groups[cat] || [];
            const groupIds = groupItems.map(g => g.id);
            const isCollapsed = !!collapsedGroups[cat] && !searchQuery;
            const allSelectedInGroup = selectMode && groupIds.length > 0 && groupIds.every(id => selectedIds.includes(id));
            return (
              <div key={cat} data-testid={`menu-group-${cat}`}>
                <div className="flex items-center gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => toggleGroup(cat)}
                    className="flex items-center gap-2 group"
                    data-testid={`menu-group-toggle-${cat}`}
                  >
                    {isCollapsed ? <ChevronRight className="w-4 h-4 text-charcoal/50" /> : <ChevronDown className="w-4 h-4 text-charcoal/50" />}
                    <h3 className="font-playfair text-lg font-semibold text-charcoal group-hover:text-terracotta transition-colors">
                      {catDisplayNames[cat] || cat}
                    </h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta font-semibold">
                      {groupItems.length}
                    </span>
                  </button>
                  {selectMode && (
                    <button
                      type="button"
                      onClick={() => {
                        if (allSelectedInGroup) setSelectedIds(prev => prev.filter(id => !groupIds.includes(id)));
                        else selectAllInGroup(groupIds);
                      }}
                      className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${allSelectedInGroup ? 'border-terracotta bg-terracotta/10 text-terracotta' : 'border-stone-300 text-charcoal/60 hover:border-terracotta hover:text-terracotta'}`}
                      data-testid={`select-group-${cat}`}
                    >
                      {allSelectedInGroup ? 'Grubu Kaldır' : 'Grubu Seç'}
                    </button>
                  )}
                  <div className="flex-1 h-px bg-stone-200 ml-2" />
                </div>
                {!isCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupItems.map((item) => {
                      const isSelected = selectedIds.includes(item.id);
                      const isDragOver = dragOverId === item.id && dragItemId && dragItemId !== item.id;
                      return (
                        <div
                          key={item.id}
                          draggable={!selectMode && !searchQuery}
                          onDragStart={() => onDragStart(item.id)}
                          onDragOver={(e) => onDragOverItem(e, item.id)}
                          onDragLeave={() => setDragOverId(null)}
                          onDrop={() => onDropItem(item.id, groupItems)}
                          onDragEnd={onDragEnd}
                          className={`border rounded-xl overflow-hidden hover:shadow-md transition-all ${item.is_available === false ? 'opacity-60' : ''} ${isSelected ? 'ring-2 ring-terracotta border-terracotta' : ''} ${isDragOver ? 'ring-2 ring-basil border-basil scale-[1.02]' : ''} ${!selectMode && !searchQuery ? 'cursor-move' : 'cursor-pointer'}`}
                          onClick={() => { if (selectMode) toggleSelected(item.id); else handleEdit(item); }}
                          data-testid={`menu-card-${item.id}`}
                        >
                          <div className="w-full h-32 bg-stone-100 relative">
                            {selectMode && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleSelected(item.id); }}
                                className={`absolute top-2 left-2 z-10 w-7 h-7 rounded-full flex items-center justify-center shadow-md ${isSelected ? 'bg-terracotta text-white' : 'bg-white/90 text-charcoal/50'}`}
                                data-testid={`select-item-${item.id}`}
                              >
                                {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                              </button>
                            )}
                            {!selectMode && !searchQuery && (
                              <div className="absolute top-2 right-2 z-10 p-1 bg-white/70 backdrop-blur rounded text-charcoal/60" title="Sürükleyerek sırala">
                                <GripVertical className="w-3.5 h-3.5" />
                              </div>
                            )}
                            {item.image_url ? (
                              <img src={item.image_url.startsWith('/api') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url} alt={item.name} className="w-full h-full object-cover absolute inset-0" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-12 h-12 text-stone-300" /></div>
                            )}
                            {item.is_available === false && (
                              <div className="absolute top-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-semibold" style={{ display: selectMode ? 'none' : undefined }}>Satista Degil</div>
                            )}
                            {item.show_in_catering && !selectMode && (
                              <div className="absolute top-2 right-2 px-2 py-0.5 bg-amber-600 text-white text-xs rounded-full font-semibold">Catering</div>
                            )}
                            {item.is_featured && (
                              <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[10px] rounded-full font-bold shadow-md flex items-center gap-1">
                                <span>⭐</span> ŞEFİN ÖNERİSİ
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-semibold">{item.name}</h3>
                              <span className="font-bold text-terracotta">€{item.price}</span>
                            </div>
                            <p className="text-xs text-charcoal/60 mb-2 line-clamp-2">{item.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs px-2 py-1 bg-sand rounded-full">{catDisplayNames[item.category] || item.category}</span>
                              {!selectMode && (
                                <div className="flex gap-1">
                                  <button onClick={(e) => { e.stopPropagation(); handleEdit(item); }} className="p-2 hover:bg-stone-100 rounded-lg text-charcoal/60 hover:text-charcoal" data-testid={`edit-item-${item.id}`}><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-item-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Category Management Panel */}
      <Dialog open={showCategoryPanel} onOpenChange={setShowCategoryPanel}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-basil" />Kategori Yönetimi
            </DialogTitle>
          </DialogHeader>
          <CategoryManagement token={token} onChange={() => { fetchCategories(); fetchMenu(); }} />
        </DialogContent>
      </Dialog>

      {/* Bulk Category Change Dialog */}
      <Dialog open={showBulkCatDialog} onOpenChange={(o) => { if (!o) setShowBulkCatDialog(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-terracotta">
              <MoveRight className="w-5 h-5" />Kategori Değiştir
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-terracotta/5 border border-terracotta/20 rounded-xl p-3 text-sm">
              <span className="font-semibold">{selectedIds.length} ürün</span> seçili.
              Bunları hangi kategoriye taşımak istiyorsunuz?
            </div>
            <div>
              <Label className="text-xs">Hedef Kategori</Label>
              <Select value={bulkTargetCat} onValueChange={setBulkTargetCat}>
                <SelectTrigger className="mt-1" data-testid="bulk-target-cat-trigger"><SelectValue placeholder="Seçin..." /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c} value={c} data-testid={`bulk-target-cat-${c}`}>{catDisplayNames[c] || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2 border-t border-stone-200">
              <Button variant="outline" className="flex-1" onClick={() => setShowBulkCatDialog(false)}>Vazgeç</Button>
              <Button onClick={submitBulkCategory} disabled={!bulkTargetCat}
                className="flex-1 bg-terracotta text-white hover:bg-terracotta/90 disabled:opacity-50"
                data-testid="bulk-cat-confirm">
                Taşı
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default MenuManagement;
